import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";

import { updateStaff } from "../../../../helpers/fakebackend_helper";
import SettingsPanel from "../../components/SettingsPanel";

const PIN_MIN = 4;
const PIN_MAX = 6;

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser"));
  } catch {
    return null;
  }
};

// Remember the new PIN in the cached authUser so the "PIN set" status stays
// accurate without a re-login.
const rememberPinLocally = (pin) => {
  try {
    const authUser = readAuthUser();
    if (!authUser?.get_staff_data) return;
    authUser.get_staff_data.EposLoginPin = pin;
    localStorage.setItem("authUser", JSON.stringify(authUser));
  } catch {
    /* cache only - safe to ignore */
  }
};

const ClockInPinSettings = () => {
  const authUser = readAuthUser();
  const staffId = authUser?.get_staff_data?.Staff_Id;

  const [hasPin, setHasPin] = useState(
    Boolean(authUser?.get_staff_data?.EposLoginPin)
  );
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handlePinChange = (setter, field) => (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, PIN_MAX);
    setter(digitsOnly);
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const nextErrors = {};
    if (pin.length < PIN_MIN || pin.length > PIN_MAX) {
      nextErrors.pin = `PIN must be ${PIN_MIN} to ${PIN_MAX} digits`;
    }
    if (confirmPin !== pin) {
      nextErrors.confirmPin = "PINs must match";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || saving) return;

    setSaving(true);
    try {
      const response = await updateStaff([{ Id: staffId, EposLoginPin: pin }]);
      if (response?.status === 200) {
        toast.success(hasPin ? "PIN updated" : "PIN set");
        rememberPinLocally(pin);
        setHasPin(true);
        setPin("");
        setConfirmPin("");
      } else {
        toast.error(response?.message || "Could not save your PIN");
      }
    } catch {
      toast.error("Could not save your PIN");
    } finally {
      setSaving(false);
    }
  };

  const visibilityToggle = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPin((prev) => !prev)}>
        {showPin ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <SettingsPanel
      title="Clock-In PIN"
      description="Set your PIN for the clock in/out kiosk."
    >
      <Container maxWidth="sm">
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h5" align="center" gutterBottom>
              {hasPin ? "Update PIN" : "Set PIN"}
            </Typography>
            <Typography
              variant="body2"
              align="center"
              color="text.secondary"
              gutterBottom
            >
              Your PIN identifies you on the shared clock in/out kiosk. It must
              be {PIN_MIN} to {PIN_MAX} digits.
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Alert severity={hasPin ? "success" : "info"}>
                {hasPin
                  ? "You have a PIN set. Saving a new one replaces it."
                  : "You don't have a PIN yet. Set one below to use the clock in/out kiosk."}
              </Alert>
            </Box>

            {!staffId ? (
              <Box sx={{ mt: 2 }}>
                <Alert severity="warning">
                  Your login isn't linked to a staff record, so a PIN can't be
                  saved. Please ask your administrator.
                </Alert>
              </Box>
            ) : (
              <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="New PIN"
                  type={showPin ? "text" : "password"}
                  value={pin}
                  onChange={handlePinChange(setPin, "pin")}
                  error={!!errors.pin}
                  helperText={errors.pin}
                  inputProps={{ inputMode: "numeric", maxLength: PIN_MAX }}
                  InputProps={{ endAdornment: visibilityToggle }}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Confirm PIN"
                  type={showPin ? "text" : "password"}
                  value={confirmPin}
                  onChange={handlePinChange(setConfirmPin, "confirmPin")}
                  error={!!errors.confirmPin}
                  helperText={errors.confirmPin}
                  inputProps={{ inputMode: "numeric", maxLength: PIN_MAX }}
                  InputProps={{ endAdornment: visibilityToggle }}
                />

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={saving}
                  >
                    {saving ? "Saving…" : hasPin ? "Update PIN" : "Set PIN"}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </SettingsPanel>
  );
};

export default ClockInPinSettings;
